import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { X, UserIcon, Wallet, Users, Trophy, Phone, LogOut } from 'lucide-react';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose, onLogout }) => {
  const navigate = useNavigate();

  const menuItems = [
    { icon: <UserIcon className="w-5 h-5" />, label: 'Profile', path: '/dashboard/profile', color: 'text-purple-400' },
    { icon: <Wallet className="w-5 h-5" />, label: 'Wallet', path: '/dashboard/wallet', color: 'text-yellow-500' },
    { icon: <Users className="w-5 h-5" />, label: 'My Team', path: '/dashboard/my-team', color: 'text-blue-400' },
    { icon: <Trophy className="w-5 h-5" />, label: 'Leaderboard', path: '/dashboard/leaderboard', color: 'text-yellow-500' },
    { icon: <Phone className="w-5 h-5" />, label: 'Support', path: '/support', color: 'text-green-400' },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: "spring", damping: 25 }}
            className="fixed top-0 left-0 bottom-0 w-72 bg-gradient-to-b from-[#1a1a2e] to-[#0a0a0f] border-r border-purple-500/20 z-50 shadow-2xl"
          >
            <div className="p-5">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-white font-rajdhani font-bold text-xl">Menu</h2>
                  <p className="text-gray-500 text-xs mt-1">Welcome back!</p>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-2">
                {menuItems.map((item) => (
                  <motion.button
                    key={item.label}
                    whileHover={{ scale: 1.05, x: 5 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      navigate(item.path);
                      onClose();
                    }}
                    className="flex items-center gap-3 w-full p-3 rounded-xl bg-white/5 hover:bg-white/10 transition text-white group"
                  >
                    <div className={item.color}>{item.icon}</div>
                    <span className="font-rajdhani font-bold">{item.label}</span>
                  </motion.button>
                ))}
                
                <div className="h-px bg-white/10 my-3" />
                
                <motion.button
                  whileHover={{ scale: 1.05, x: 5 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    onLogout();
                    onClose();
                  }}
                  className="flex items-center gap-3 w-full p-3 rounded-xl bg-red-500/10 hover:bg-red-500/20 transition text-red-400 mt-2"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-rajdhani font-bold">Logout</span>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileMenu;